import type React from "react";
import { useEffect, useState } from "react";
import co2CalculatorImg from "../../assets/co2-calculator.png";
import logoImg from "../../assets/logo.png";
import { calculator } from "../../lib/calculator";
import { useI18n } from "../../lib/i18n";
import { furnitureValidator } from "../../lib/validation";
import { LanguageSelector } from "../introduction/LanguageSelector";
import FurnitureInputForm, { type FurnitureData } from "./FurnitureInputForm";
import ResultsTable, { type CalculationResult } from "./ResultsTable";

/**
 * Props for CalculatorPage component
 */
interface CalculatorPageProps {
  onNavigateToIntroduction: () => void;
}

/**
 * Main calculator page
 * Combines input form and results display
 * Automatic real-time calculation
 */
const CalculatorPage: React.FC<CalculatorPageProps> = ({
  onNavigateToIntroduction,
}) => {
  const { t, isLoaded, language } = useI18n();

  const emptyFurnitureData: FurnitureData = {
    fte: 0,
    officeArea: 0,
    cabinets: 0,
    individualDesks: 0,
    officeChairs: 0,
    canteenTables: 0,
    meetingTables: 0,
    visitorChairs: 0,
  };

  const sampleFurnitureData: FurnitureData = {
    fte: 50,
    officeArea: 780,
    individualDesks: 50,
    officeChairs: 50,
    cabinets: 40,
    canteenTables: 10,
    meetingTables: 3,
    visitorChairs: 70,
  };

  const [furnitureData, setFurnitureData] =
    useState<FurnitureData>(emptyFurnitureData);

  const [results, setResults] = useState<CalculationResult | null>(null);

  // Load saved data when component mounts
  useEffect(() => {
    const savedData = sessionStorage.getItem("transmoove-furniture-data");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFurnitureData({ ...emptyFurnitureData, ...parsedData });
      } catch (error) {
        console.error("Error loading saved data:", error);
      }
    } else {
      setFurnitureData(sampleFurnitureData);
      sessionStorage.setItem(
        "transmoove-furniture-data",
        JSON.stringify(sampleFurnitureData),
      );
    }
  }, []);

  // Automatic calculation of results when data changes
  useEffect(() => {
    // Check if we have minimum data to calculate
    if (furnitureValidator.hasMinimumData(furnitureData)) {
      const calculationResult = calculator.calculate(furnitureData);
      setResults(calculationResult);
    } else {
      setResults(null);
    }
  }, [furnitureData]);

  const handleContactClick = () => {
    // Adapt link according to language
    const contactUrl =
      language === "fr"
        ? "https://www.transmoove.org/fr/contact"
        : "https://www.transmoove.org/nl/contact";

    window.open(contactUrl, "_blank");
  };

  // Loading display if translations are not yet loaded
  if (!isLoaded) {
    return (
      <div className="tm-page flex items-center justify-center">
        <p className="text-transmoove-text text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="tm-page">
      <div className="px-6 py-4">
        <div className="relative">
          <div className="absolute top-0 right-0 text-right">
            <LanguageSelector />
          </div>

          <div className="grid gap-3 lg:grid-cols-[240px_1fr] items-start">
            <div>
              <button
                type="button"
                onClick={onNavigateToIntroduction}
                className="inline-flex"
              >
                <img src={logoImg} alt="Transmoove" className="h-10 w-auto" />
              </button>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center gap-6">
                <div>
                  <div className="text-transmoove-textDark font-semibold text-tm-4xl leading-tight">
                    {t("calculator.header.titleLine1")}
                  </div>
                  <div className="text-transmoove-textDark font-semibold text-tm-4xl leading-tight">
                    {t("calculator.header.titleLine2")}
                  </div>
                  <div className="mt-1 text-tm-xs text-white">
                    {t("calculator.header.subtitle")}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex flex-col items-center">
                    <img
                      src={co2CalculatorImg}
                      alt="CO2 calculator"
                      className="h-16 w-16"
                    />
                  </div>
                  <div
                    className="text-tm-2xl text-transmoove-text p-8"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: content is translated string with controlled placeholder
                    dangerouslySetInnerHTML={{
                      __html: t("calculator.header.certificate", {
                        ecoCertificat: t("calculator.header.ecoCertificat"),
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 mx-auto">
          <div className="grid gap-4 lg:grid-cols-[1fr_260px] items-start">
            <FurnitureInputForm
              data={furnitureData}
              onDataChange={setFurnitureData}
            />

            <div className="text-right lg:justify-self-end">
              <div className="tm-bar px-3 py-2 inline-block w-[240px] text-center">
                <div className="text-tm-base font-semibold">
                  {t("calculator.header.contactTitle")}
                </div>
                <div className="text-tm-base">
                  {t("calculator.header.contactSubtitle")}
                </div>
                <a
                  href="tel:+3227053535"
                  className="tm-phone-link text-tm-2xl font-semibold underline"
                >
                  {t("calculator.header.contactPhone")}
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <ResultsTable
              results={results}
              onContactClick={handleContactClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculatorPage;
